/**
 * PDF Generator Utility for Team Reports
 * Produces an A4-landscape PDF FILE (direct download, no print dialog).
 *
 * Requirement 45: PDF download for team reports - A4 landscape per page
 *
 * 【描画エンジンに modern-screenshot を使う理由】
 * 旧実装は html2canvas でページを画像化していたが、html2canvas(1.4.1) は本テンプレート
 * を再現できず分布図などが崩れる（CSS Grid が潰れる／`::before`の`background:url(svg)
 * center/contain`グリッドが巨大化／`translate`プロパティ配置・`min()`/vw 非対応）。
 * modern-screenshot はブラウザネイティブの foreignObject 描画を用い、フォント・画像・CSS
 * を inline したうえでレイアウトをブラウザ自身に任せるため、画面表示と同一の結果を得られる
 * （Playwright + 実Chrome で忠実描画を確認済み）。
 *
 * 【本実装のフロー】
 * 1) 非表示 iframe に実ドキュメントとして描画（stylesheet / Webフォント / main.js の
 *    レーダー描画をレポートタブと同一条件で実行）
 * 2) 各セクション（1ページ）を modern-screenshot で高解像度キャプチャ
 * 3) A4横の各ページに内接配置して jsPDF に埋め込み、`save()` でファイルとして直接DL
 */

import { jsPDF } from 'jspdf';
import { domToCanvas } from 'modern-screenshot';

/**
 * キャプチャ用 iframe の表示幅。
 * レポートCSSは版面を min(rem基準, 950/1440*100vw) で決めるため、
 * 1440px 以上なら rem基準（=固定px）が採用され、版面が常に 950x670px に確定する。
 * 1440未満だと vw 項が勝ち、版面がウィンドウ側の都合で縮む（過去の崩れの根本原因）。
 */
const RENDER_WIDTH = 1440;
/** 設計版面の幅(px)。キャプチャ前の自己検証に使う。 */
const DESIGN_PAGE_WIDTH = 950;
/** キャプチャ iframe を外部CSSから守るための id。 */
const CAPTURE_FRAME_ID = 'ta74-pdf-capture-frame';
/** キャプチャ解像度倍率（文字・細線を鮮明にするため 3 ≒ 高dpi 相当） */
const CAPTURE_SCALE = 3;
/** JPEG 画質。jsPDF は JPEG を DCTDecode でそのまま埋め込むためファイルが軽い。
 *  細い罫線・文字がにじまないよう高めに設定 */
const JPEG_QUALITY = 0.95;

/**
 * Generate an A4-landscape PDF file from team report HTML and download it.
 * @param html HTML content to convert (full <!DOCTYPE html> document)
 * @param filename Output filename (e.g. "2026.08.11 組織診断結果.pdf")
 */
export async function generateTeamReportPdf(html: string, filename: string): Promise<void> {
  // レポートを非表示 iframe に「実ドキュメント」として描画する。
  // innerHTML と違い <link>/<script>/@font-face が実際に読み込み・実行される。
  const iframe = document.createElement('iframe');
  iframe.id = CAPTURE_FRAME_ID;
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.left = '-10000px';
  iframe.style.top = '0';
  iframe.style.width = `${RENDER_WIDTH}px`;
  iframe.style.minWidth = `${RENDER_WIDTH}px`;
  iframe.style.maxWidth = 'none';
  iframe.style.height = '2200px';
  iframe.style.border = '0';
  iframe.style.background = '#ffffff';
  // 【重要】キャプチャ幅がウィンドウ幅や外部CSS（例: iframe{max-width:100%}）に
  // 影響されると、vw比例のレポート版面ごと縮んで文字・図の比率が崩れる。
  // id セレクタ + !important の専用スタイルを親ドキュメントへ注入し、
  // どんなアプリ側CSSよりも優先してキャプチャ幅を固定する。
  const framePin = document.createElement('style');
  framePin.textContent = `#${CAPTURE_FRAME_ID}{width:${RENDER_WIDTH}px !important;min-width:${RENDER_WIDTH}px !important;max-width:none !important;height:2200px !important;}`;
  document.head.appendChild(framePin);
  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument;
    if (!doc) throw new Error('iframe document を取得できませんでした');

    // ドキュメント全体を書き込む（head の <link>/<style>、body、defer script を含む）
    doc.open();
    doc.write(html);
    doc.close();

    // stylesheet / フォント / 画像 / main.js（レーダー描画）が整うまで待機
    await waitForIframeReady(iframe);

    // rem の基準を固定する（viewport 1440px なら CSS の 0.694vw と同値＝見た目不変。
    // 万一 viewport が縮んでいる環境でも rem 側のブレを断つ保険）。
    const fontPin = doc.createElement('style');
    fontPin.textContent = 'html{font-size:10px !important;}';
    doc.head.appendChild(fontPin);

    // 【自己検証つきキャプチャ】実際の viewport 幅を測り、固定に失敗している環境では
    // zoom で補正して「版面950pxの設計比率」を維持する（ウィンドウ幅への依存を断つ）。
    const actualVw = doc.documentElement.clientWidth;
    if (actualVw < RENDER_WIDTH - 5) {
      const z = actualVw / RENDER_WIDTH;
      (doc.documentElement.style as CSSStyleDeclaration & { zoom: string }).zoom = String(z);
      await new Promise((r) => setTimeout(r, 350));
    }

    // 各セクション（1ページ）を取得。
    // data-pdf-skip が付いたページは PDF に含めない（例: 回答者2名未満でネットワーク図を
    // 描けない部署ページ。HTML表示では理由を明示するが、配布用PDFからは除外する）。
    const pages = Array.from(
      doc.querySelectorAll<HTMLElement>('.c-report-single_list_item:not([data-pdf-skip])')
    );
    if (pages.length === 0) throw new Error('レポートページが見つかりませんでした');

    // 版面が設計幅どおりに確定しているか自己検証（zoom 補正後は zoom 倍で測れる）。
    {
      const zoomStr = (doc.documentElement.style as CSSStyleDeclaration & { zoom?: string }).zoom;
      const z = zoomStr ? Number(zoomStr) || 1 : 1;
      const pw = pages[0].getBoundingClientRect().width / z;
      if (Math.abs(pw - DESIGN_PAGE_WIDTH) > 20) {
        console.warn(
          `[pdfGenerator] 版面幅が設計値からずれています: 実測 ${pw.toFixed(1)}px / 期待 ${DESIGN_PAGE_WIDTH}px (viewport=${doc.documentElement.clientWidth}px, zoom=${z})`
        );
      }
    }

    // A4横で PDF を作成
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = 297; // A4横 幅(mm)
    const pageHeight = 210; // A4横 高さ(mm)

    for (let i = 0; i < pages.length; i++) {
      const el = pages[i];

      // modern-screenshot でブラウザネイティブ描画（html2canvas と違いテンプレを忠実再現）
      const canvas = await domToCanvas(el, {
        scale: CAPTURE_SCALE,
        backgroundColor: '#ffffff',
        // iframe 内要素なので、その window を明示してフォント/リソース解決を安定させる
        width: el.offsetWidth,
        height: el.offsetHeight,
      });

      if (i > 0) pdf.addPage();

      // アスペクト比を保ったまま A4横ページに内接させ、中央配置
      const imgRatio = canvas.width / canvas.height;
      const pageRatio = pageWidth / pageHeight;
      let drawW: number;
      let drawH: number;
      if (imgRatio >= pageRatio) {
        drawW = pageWidth;
        drawH = pageWidth / imgRatio;
      } else {
        drawH = pageHeight;
        drawW = pageHeight * imgRatio;
      }
      const x = (pageWidth - drawW) / 2;
      const y = (pageHeight - drawH) / 2;

      // jsPDF は JPEG をそのまま埋め込む（PNG は内部で肥大化するため JPEG を使用）。
      // 背景は白で塗っているため透過は不要。
      const imgData = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
      pdf.addImage(imgData, 'JPEG', x, y, drawW, drawH);
    }

    pdf.save(filename);
  } finally {
    document.body.removeChild(iframe);
    framePin.remove();
  }
}

/**
 * iframe 内レポートの描画完了を待つ。
 * 1) load（stylesheet/script/画像の初期読込）
 * 2) Webフォント
 * 3) 画像の complete
 * 4) resize を発火して main.js のレーダー再描画を確定
 * 5) レイアウト/描画の落ち着き待ち
 */
async function waitForIframeReady(iframe: HTMLIFrameElement): Promise<void> {
  const win = iframe.contentWindow;
  const doc = iframe.contentDocument;
  if (!win || !doc) return;

  // 1) load イベント（document.write 済みなので既に complete のこともある）
  if (doc.readyState !== 'complete') {
    await new Promise<void>((resolve) => {
      const done = () => resolve();
      iframe.addEventListener('load', done, { once: true });
      setTimeout(done, 4000); // フォールバック
    });
  }

  // 2) Webフォント
  const fonts = (doc as Document & { fonts?: FontFaceSet }).fonts;
  if (fonts?.ready) {
    try {
      await fonts.ready;
    } catch {
      /* ignore */
    }
  }

  // 3) 画像
  const images = Array.from(doc.querySelectorAll('img'));
  await Promise.all(
    images.map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise<void>((resolve) => {
        img.addEventListener('load', () => resolve(), { once: true });
        img.addEventListener('error', () => resolve(), { once: true });
        setTimeout(resolve, 4000);
      });
    })
  );

  // 4) main.js はロード時にレーダーを clientWidth 基準で描画する。
  //    最終レイアウト確定後に再描画させるため resize を発火。
  try {
    win.dispatchEvent(new Event('resize'));
  } catch {
    /* ignore */
  }

  // 5) 落ち着き待ち（レーダー再描画・レイアウト安定）
  await new Promise((resolve) => setTimeout(resolve, 600));
}
